// Shared error helpers for the login / reset-password screens.
//
// The backend reports failures in a few shapes, for example:
//   { "error": "The user has no portal access" }
//   { "message": "..." } / { "detail": "..." }
//   { "non_field_errors": ["..."] } / { "password": ["..."] }
// These helpers pull out that text so the screens show what the backend
// actually said instead of a hard-coded generic message.

// Parses a fetch Response body as JSON without throwing on HTML/empty bodies.
export const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const toText = (value) => {
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    const parts = value.map(toText).filter(Boolean);
    return parts.length ? parts.join(" ") : null;
  }
  if (value && typeof value === "object") {
    return getBackendErrorMessage(value);
  }
  return null;
};

// Returns the backend's error text, or null when the body carries none.
export const getBackendErrorMessage = (res) => {
  if (!res || typeof res !== "object" || Array.isArray(res)) {
    return toText(res);
  }
  for (const key of ["error", "message", "detail", "non_field_errors", "errors"]) {
    const text = toText(res[key]);
    if (text) return text;
  }
  // Field-level validation errors such as { "password": ["Too short."] }.
  for (const value of Object.values(res)) {
    if (Array.isArray(value)) {
      const text = toText(value);
      if (text) return text;
    }
  }
  return null;
};

// Backend text when present, otherwise the screen's own fallback message.
export const errorMessageFor = (res, fallback) =>
  getBackendErrorMessage(res) || fallback;

// Like errorMessageFor, but only trusts the body of a failed response, so a
// successful payload's "message" field is never shown as an error.
export const responseErrorMessage = (response, res, fallback) =>
  response?.ok ? fallback : errorMessageFor(res, fallback);

// Thrown for a non-OK response so a catch block can tell it apart from a
// network or parse failure and show the backend's message as-is.
export class BackendRequestError extends Error {}
